import {Injectable} from '@angular/core';
import {BehaviorSubject,Observable} from "rxjs";
import {MessageAdmin} from "@domain/admin/bibliotheque/notification/message-admin";
import {NotificationService} from "@services/admin/bibliotheque/notification.service";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {ActivatedRoute,Router} from "@angular/router";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {filter,finalize,first} from "rxjs/operators";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {handleResult} from "@share/utils/handle-result";
import {RoleItemComponent} from "@components/admin/bibliotheque/notifications/accueil/accueil-detail/role-item/role-item.component";
import {ListView,TypeComparaison} from "@domain/common/list-view";
import {Sorting} from "@domain/common/list-view/sorting";
import {MessageAdminRole} from "@domain/admin/bibliotheque/notification/message-admin-role";
import {TypeProfil} from "@domain/user/user";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {AddRolePopinComponent} from "@components/admin/bibliotheque/notifications/accueil/accueil-detail/add-role-popin/add-role-popin.component";

/**
 * Service de gestion du détail de l'accueil
 *
 * @author Lionel Gimenez
 * @date 24/03/2026
 */
@Injectable()
export class AccueilDetailService {
    /** Indique si le service est en train de sauvegarder */
    isSaving: boolean = false;

    /** Indicateur de création */
    isCreation: boolean = false;

    /** Modèle du message admin */
    private messageAdminSubject: BehaviorSubject<MessageAdmin> = new BehaviorSubject<MessageAdmin>(null);

    /** Getter accueil */
    get accueil$(): Observable<MessageAdmin> {
        return this.messageAdminSubject.asObservable();
    }

    /** Référence à la liste des rôles */
    listeRolesRef: ListView<MessageAdminRole, RoleItemComponent>;

    /** Constructeur
     *
     * @param notificationService Service d'accès aux messages d'administration
     * @param translateService Service de traduction
     * @param toastrService Service d'affichage des notifications
     * @param activatedRoute Route active
     * @param matDialog Service Angular de popin
     * @param confirmService Service de demande de confirmation
     * @param router Service de navigation
     *
     * */
    constructor(
        private notificationService: NotificationService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private activatedRoute: ActivatedRoute,
        private matDialog: MatDialog,
        private confirmService: ConfirmService,
        private router: Router
    ) {
    }

    /**
     * Charge l'accueil à partir de la route
     */
    loadAccueilByRoute(): void {
        //Chargement de l'id de l'accueil
        const idAccueil: number = this.activatedRoute.snapshot.params.idAccueil;

        //Si on est en création
        if (idAccueil == 0) {
            this.loadNewAccueil();
        } else {
            //Chargement de l'accueil
            this.loadAccueilById(idAccueil);
        }
    }

    /**
     * Charge un nouvel accueil
     */
    loadNewAccueil() {
        //On change l'indicateur
        this.isCreation = true;

        //Initialisation du message, actif par défaut à la création
        const messageAdmin = new MessageAdmin();
        messageAdmin.actif = true;

        //Chargement du nouveau message admin
        this.messageAdminSubject.next(messageAdmin);
    }

    /**
     * Charge l'accueil par l'id
     */
    loadAccueilById(id: number) {
        this.notificationService.loadMessageAdmin(id)
            .pipe(
                first(),
                finalize(() => this.isSaving = false))
            .subscribe((result: Result) => {
                this.messageAdminSubject.next(result.data.messageAdmin);
            });
    }

    /**
     * Sauvegarde l'accueil
     */
    save(): void {
        //Message à enregistrer
        const messageAdmin = this.messageAdminSubject.getValue();

        //Enregistrement en cours
        this.isSaving = true;

        //Enregistrement du message
        this.notificationService
            .saveMessageAdmin(messageAdmin)
            .pipe(
                handleResult(this.translateService,this.toastrService),
                finalize(() => this.isSaving = false)
            ).subscribe((result: Result) => {
                //Vérification du retour pour recharger la page le cas échéant
                if (result?.codeErreur == TypeCodeErreur.NO_ERROR) {
                    //Rechargement de la page si on est en création
                    if (messageAdmin.idMessage == null) {
                        this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
                            this.router.navigate(['Admin/Bibliotheque/Notification/Accueil', result.data.idMessage]);
                        });
                    }
                }
            });
    }

    /**
     * Initialisation de la liste des rôles
     */
    public initListeRoles() {
        //Récupération de l'id de l'accueil
        const idAccueil: number = this.activatedRoute.snapshot.params.idAccueil;

        //Si on n'est pas en création
        if (idAccueil != 0) {
            //Initialisation de la liste des rôles
            const listeView = new ListView<MessageAdminRole, RoleItemComponent>({
                uri: `/controller/MessageAdmin/loadListeRole/${idAccueil}`,
                title: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.titleRoles'),
                component: RoleItemComponent,
                defaultOrder: 'fonction',
                emptyMessage: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.tousRoles'),
                listeActions: [{
                    icon: "add",
                    onPress: () => this.addRole()
                }],
                listeFilters: [
                    {
                        clef: 'fonction',
                        title: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.fonction'),
                        listeValues: [
                            {
                                code: '' + TypeProfil.ASSISTANT,
                                libelle: TypeProfil.getLibelle(this.translateService,TypeProfil.ASSISTANT)
                            },{
                                code: '' + TypeProfil.COLLABORATEUR,
                                libelle: TypeProfil.getLibelle(this.translateService,TypeProfil.COLLABORATEUR)
                            },{
                                code: '' + TypeProfil.COMPTABLE,
                                libelle: TypeProfil.getLibelle(this.translateService,TypeProfil.COMPTABLE)
                            },{
                                code: '' + TypeProfil.FOURNISSEUR,
                                libelle: TypeProfil.getLibelle(this.translateService,TypeProfil.FOURNISSEUR)
                            },{
                                code: '' + TypeProfil.RESPONSABLE,
                                libelle: TypeProfil.getLibelle(this.translateService,TypeProfil.RESPONSABLE)
                            },{
                                code: '' + TypeProfil.SOUS_ADMINISTRATEUR,
                                libelle: TypeProfil.getLibelle(this.translateService,TypeProfil.SOUS_ADMINISTRATEUR)
                            }
                        ]
                    },{
                        clef: 'libelle',
                        title: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.libelle'),
                        typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                        isDefault: true
                    }
                ]
            });

            //Définition des colonnes de tri
            listeView.columns = [
                {key: 'fonction',title: 'admin.bibliotheque.notifications.accueil.detail.fonction'},
                {key: 'libelle',title: 'admin.bibliotheque.notifications.accueil.detail.libelle'}
            ];

            //Ajout du tri de la liste selon l'ordre voulu
            listeView.sorting = new Sorting(listeView.columns,'fonction');

            //Sauvegarde de la référence
            this.listeRolesRef = listeView;

            return listeView;
        }

        return null;
    }

    /**
     * Suppression de l'accueil
     */
    delete(): void {
        //Récupération de l'id du message
        const idMessage: number = this.activatedRoute.snapshot.params.idAccueil;

        //On demande une confirmation de suppression
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe(() => {
                //Suppression en cours
                this.isSaving = true;

                //Suppression du message
                this.notificationService.deleteMessage(idMessage)
                    .pipe(first())
                    .subscribe((result: Result) => {
                        if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                            //Toast succès
                            this.toastrService.success(this.translateService.instant('global.success.suppression'));

                            //Retour à la liste des accueils
                            this.router.navigate(['Admin/Bibliotheque/Notification/Accueil']);
                        } else {
                            //Echec de la suppression
                            this.isSaving = false;

                            //Toast d'erreur
                            TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
                        }
                    });
            });
    }

    duplicate() {
        //Récupération de l'id du message
        const idMessage: number = this.activatedRoute.snapshot.params.idAccueil;

        this.notificationService
            .duplicateMessage(idMessage)
            .pipe(
                handleResult(this.translateService,this.toastrService)
            ).subscribe((result: Result) => {
                //Rechargement de la page si on est en création
                if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
                        this.router.navigate(['Admin/Bibliotheque/Notification/Accueil', result.data.idMessage]);
                    });
                }
            });
    }

    /**
     * Ouvre la popin d'ajout de rôles
     */
    addRole() {
        //Récupération de l'id du message
        const idMessage: number = this.activatedRoute.snapshot.params.idAccueil;

        //Ouverture de la popin
        this.matDialog.open(AddRolePopinComponent,{
            data: { idMessage },
            width: '80%'
        }).afterClosed().subscribe((listeRoles: MessageAdminRole[]) => {
            //Si des rôles ont été sélectionnés
            if (listeRoles?.length) {
                //Sauvegarde des rôles
                const listeRoleId: number[] = listeRoles.map(role => role.idRole);
                this.notificationService.saveRoles(idMessage, listeRoleId)
                    .pipe(handleResult(this.translateService, this.toastrService))
                    .subscribe(() => this.listeRolesRef?.refresh());
            }
        });
    }

    /**
     * Envoi du message de notification par email
     */
    sendEmail(): void {
        //Message à envoyer
        const messageAdmin = this.messageAdminSubject.getValue();

        //Indique si des rôles sont sélectionnés
        const hasRole = this.listeRolesRef?.data?.listeResultats?.length > 0;

        //Affichage de la confirmation
        this.confirmService.showConfirm(this.translateService.instant(`admin.bibliotheque.notifications.accueil.detail.emailSaveConfirm${hasRole ? 'AvecRole' : 'SansRole'}`))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe(() => {
                //Enregistrement en cours
                this.isSaving = true;

                //Envoi de l'email
                this.notificationService.sendEmail(messageAdmin.idMessage)
                    .pipe(
                        handleResult(this.translateService,this.toastrService,'global.success.generic','global.errors.generic'),
                        finalize(() => this.isSaving = false)
                    ).subscribe(result => {
                        //Traitement du retour en cas de succès
                       if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                           //Mise à jour du message
                           messageAdmin.email = true;
                           messageAdmin.dateEnvoi = result.data.dateEnvoi;

                           //Mise à jour du message dans le subject
                           this.messageAdminSubject.next(messageAdmin);
                       }
                    });
            });
    }
}
